import type { RefObject } from 'react';
import { V4 } from '../tokens';

// ── ErrorSummary ───────────────────────────────────────────────────────────
// Clickable error summary (GOV.UK pattern): lists every field blocking the save, one row per
// field ("› CLABE interbancaria — Transferencia"). Visible while there are errors and unsaved
// changes; receives focus (tabIndex -1 + role alert) after a failed save attempt so screen
// readers announce it.
//
// It lives next to EditPageLayout because that is where its two page consumers are, but it does
// NOT require the layout: the third consumer is the `modals/Categories.tsx` modal, whose rows
// NAVIGATE OUT of the route instead of activating a section — a surface that cannot edit the
// offending field has no section to take you to. What this component owns is the list and how it
// behaves; where a row leads is decided by whoever mounts it.
//
// The page owns WHAT is wrong (its validator + its error map) and WHERE each field lives; this
// component owns only how the list looks and behaves. It was extracted from `DeliveryDataV2` when
// `CommerceDataV2` became the second page to need it — duplicating 60 lines of styling is how the
// two summaries would have drifted apart. Spec of the visual contract: DESIGN.md §"Contrato de
// validación — hasErrors" point 7.
export type ErrorSummaryItem = {
  // Stable DOM id of the field the row points at. Doubles as the React key, so rows pointing at
  // the same field must be deduped by the caller's error map before reaching this component.
  fieldId: string;
  // Field name (Spanish, user-facing).
  label: string;
  // Section that owns the field. Rendered after the label; suppressed when it repeats the label
  // (a whole-section error like "Métodos de entrega" would otherwise read twice).
  sectionLabel: string;
};

export type ErrorSummaryProps<T extends ErrorSummaryItem> = {
  items: T[];
  onItemClick: (item: T) => void;
  summaryRef: RefObject<HTMLDivElement>;
};

export function ErrorSummary<T extends ErrorSummaryItem>({ items, onItemClick, summaryRef }: ErrorSummaryProps<T>) {
  return (
    <div
      ref={summaryRef}
      role="alert"
      tabIndex={-1}
      style={{
        border: `1.5px solid ${V4.danger}`,
        background: V4.dangerSurface,
        borderRadius: 10,
        padding: '12px 14px',
        outline: 'none',
      }}
    >
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 700, color: V4.dangerText }}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
          <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
        {items.length === 1
          ? 'No se puede guardar todavía: falta 1 campo'
          : `No se puede guardar todavía: faltan ${items.length} campos`}
      </div>
      <ul
        style={{
          margin: '8px 0 0',
          padding: 0,
          listStyle: 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {items.map((item) => (
          <li key={item.fieldId}>
            <button
              type="button"
              onClick={() => onItemClick(item)}
              style={{
                background: 'transparent',
                border: 0,
                padding: '3px 0',
                color: V4.dangerText,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
                display: 'inline-flex',
                alignItems: 'baseline',
                gap: 6,
                textAlign: 'left' as const,
              }}
            >
              <span aria-hidden="true">›</span>
              <span style={{ textDecoration: 'underline' }}>
                {item.label}
                {item.sectionLabel !== item.label && (
                  <span style={{ fontWeight: 500, opacity: 0.8 }}> — {item.sectionLabel}</span>
                )}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
